import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { onEvent } from "../socket";
import { timeAgo } from "../config";
import { Spinner, Empty } from "../components/cards";

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setItems(Array.isArray(r) ? r : r.notifications || [])).catch(() => setItems([]));
    const off = onEvent("notification", (ev) => {
      if (ev.notification) setItems((list) => [ev.notification, ...(list || [])]);
    });
    return off;
  }, [user]);

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((list) => (list || []).map((n) => ({ ...n, read: true })));
    } catch {}
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-md py-20" data-testid="notifications-signin-notice">
        <Empty>
          <p className="text-center">Sign in to see your notifications.</p>
          <button onClick={() => navigate("/app/login")} className="mx-auto mt-4 block rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="notifications-signin-button">
            Sign in
          </button>
        </Empty>
      </div>
    );
  }

  if (items === null) return <Spinner />;

  return (
    <div className="mx-auto max-w-2xl" data-testid="notifications-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Notifications</h1>
        {items.length > 0 && (
          <button onClick={readAll} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-crimson/50 hover:text-white" data-testid="read-all-button">
            <CheckCheck size={14} /> Mark all read
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <Empty>
          <Bell size={20} className="mx-auto mb-3 text-slate-600" />
          No notifications yet.
        </Empty>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const inner = (
              <div className={`flex items-start gap-3 rounded-2xl border p-4 transition-colors ${n.read ? "border-white/5 bg-white/[0.01]" : "border-crimson/25 bg-crimson/[0.06]"}`} data-testid={`notification-${n.id}`}>
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-white/5 text-slate-500" : "bg-crimson/20 text-crimson"}`}>
                  <Bell size={14} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{n.title || n.type || "Notification"}</p>
                  {(n.body || n.message) && <p className="mt-0.5 text-sm text-slate-400">{n.body || n.message}</p>}
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-600">{timeAgo(n.createdAt)}</p>
                </div>
              </div>
            );
            return n.link ? (
              <Link key={n.id} to={n.link}>{inner}</Link>
            ) : (
              <div key={n.id}>{inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Notifications;
